import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence, useAnimationControls, useReducedMotion } from "framer-motion";
import { productUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useActivity, openAuth } from "../lib/activity";
import { tapHaptic, successHaptic } from "../lib/native";
import IntentDateModal, { formatMonthLabel } from "./IntentDateModal";

const SIZES = {
  sm: { box: "min-h-[40px]", fist: "w-10 text-lg", line1: "text-[length:var(--fs-meta)]", line2: "hidden", count: "text-sm" },
  md: { box: "min-h-[52px]", fist: "w-[52px] text-2xl", line1: "text-[length:var(--fs-chat)]", line2: "text-[length:var(--fs-meta)]", count: "text-base" },
  lg: { box: "min-h-[64px]", fist: "w-16 text-[length:var(--fs-fist)]", line1: "text-[length:var(--fs-cta)]", line2: "text-[length:var(--fs-small)]", count: "text-xl" },
};

const PENDING_KEY = "bhega:pendingJoin";
const NUDGED_KEY = "bhega:nudged";
const PENDING_TTL = 10 * 60 * 1000;
const TAP_SPRING = { type: "spring", stiffness: 600, damping: 30 };
const HOVER_SPRING = { type: "spring", stiffness: 400, damping: 25 };
const LABEL_SPRING = { type: "spring", stiffness: 500, damping: 30 };

// One attention nudge per page view (and per session, via sessionStorage).
let nudgeClaimed = false;

const ss = {
  get(k) { try { return window.sessionStorage.getItem(k); } catch (_) { return null; } },
  set(k, v) { try { window.sessionStorage.setItem(k, v); } catch (_) { /* noop */ } },
  del(k) { try { window.sessionStorage.removeItem(k); } catch (_) { /* noop */ } },
};

function DrawnCheck() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <motion.path d="M20 6 9 17l-5-5" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.28, ease: "easeOut" }} />
    </svg>
  );
}

// The primary call-to-action of BHEGA: raise your fist to join buyers of the same product.
// Tapping opens the month sheet in place; the server join happens inside IntentDateModal.
export default function BhegaButton({ product, count = 0, size = "lg", full = true, testId, countTestId, onClick }) {
  const navigate = useNavigate();
  const { user, city } = useAuth();
  const activity = useActivity();
  const reduce = useReducedMotion();
  const fist = useAnimationControls();
  const btnRef = useRef(null);
  const [punch, setPunch] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [nudging, setNudging] = useState(false);
  const [announce, setAnnounce] = useState("");

  const id = product?.id;
  const name = product?.product || product?.name || "this product";
  const isAdmin = user?.role === "admin";
  const joinedIds = activity?.joinedIds;
  const localJoin = activity?.localJoins?.[id]; // "YYYY-MM" | "ASAP" | "pending" | undefined
  // Room-page buttons (onClick override) exist to move a "pending" member to a real month, so stay idle for them.
  const joined = !!joinedIds?.has(id) && !(onClick && localJoin === "pending" && !(activity?.rooms || []).some((r) => r.product_id === id));
  const room = (activity?.rooms || []).find((r) => r.product_id === id);
  const localMonth = localJoin && localJoin !== "pending"
    ? (localJoin === "ASAP" ? "ASAP" : formatMonthLabel(localJoin))
    : "";
  const month = room?.month_label || localMonth;
  const s = SIZES[size] || SIZES.lg;

  // Signed out → signed in: reopen the sheet for the product the user tapped.
  useEffect(() => {
    if (!user || !id || onClick) return;
    if (activity?.loadedFor !== user.id) return; // wait until the joined list belongs to this user
    const raw = ss.get(PENDING_KEY);
    if (!raw) return;
    let data = null;
    try { data = JSON.parse(raw); } catch (_) { ss.del(PENDING_KEY); return; }
    if (data?.id !== id) return;
    ss.del(PENDING_KEY); // remove first so only one button reacts
    if (Date.now() - (data.t || 0) > PENDING_TTL || isAdmin || joined) return;
    setSheetOpen(true);
  }, [user, activity?.loadedFor, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attention nudge: first unjoined button ≥60% visible and still for 1.5s. Once per session.
  useEffect(() => {
    const el = btnRef.current;
    if (!el || isAdmin || joined || !activity?.loaded || (joinedIds?.size || 0) > 0) return undefined;
    if (nudgeClaimed || ss.get(NUDGED_KEY) || typeof IntersectionObserver === "undefined") return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    let timer = null;
    let visible = false;
    const fire = () => {
      if (nudgeClaimed || ss.get(NUDGED_KEY)) return;
      nudgeClaimed = true;
      ss.set(NUDGED_KEY, "1");
      setNudging(true);
      fist.start({ y: [0, -4, 0], rotate: [0, -10, 0], transition: { duration: 0.7, times: [0, 0.4, 1], ease: "easeOut" } });
    };
    const arm = () => { clearTimeout(timer); if (visible && !nudgeClaimed) timer = setTimeout(fire, 1500); };
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= 0.6;
      arm();
    }, { threshold: 0.6 });
    io.observe(el);
    window.addEventListener("scroll", arm, { passive: true });
    return () => { io.disconnect(); clearTimeout(timer); window.removeEventListener("scroll", arm); };
  }, [isAdmin, joined, activity?.loaded, joinedIds?.size, fist]);

  const afterJoin = (label) => {
    setSheetOpen(false);
    successHaptic();
    setAnnounce(label ? `You're in · ${label}` : "You're in");
    if (!reduce) fist.start({ scale: [1, 1.3, 1], rotate: [0, -14, 0], transition: { duration: 0.45 } });
  };

  const handle = (e) => {
    e.preventDefault(); e.stopPropagation();
    setPunch((n) => n + 1);
    tapHaptic();
    if (onClick) { onClick(); return; }
    if (!user) {
      ss.set(PENDING_KEY, JSON.stringify({ id, t: Date.now() }));
      toast("Sign in to raise your fist and join this Bhega");
      openAuth();
      return;
    }
    if (isAdmin || joined) { navigate(productUrl(product)); return; }
    setSheetOpen(true);
  };

  const ariaLabel = joined
    ? `You're in the ${name} group with ${count} buyers${month ? `, buying ${month}` : ""}. Open group chat.`
    : `I want to buy ${name}. Join ${count} buyers. Free.`;
  const labelTransition = reduce ? { duration: 0.15 } : LABEL_SPRING;

  return (
    <>
      <motion.button
        ref={btnRef}
        type="button"
        onClick={handle}
        data-testid={testId}
        data-nudging={nudging ? "1" : undefined}
        aria-label={ariaLabel}
        aria-haspopup={!joined && !onClick ? "dialog" : undefined}
        aria-expanded={!joined && !onClick ? sheetOpen : undefined}
        whileHover="hover"
        whileTap={reduce ? undefined : { scale: 0.97 }}
        transition={TAP_SPRING}
        className={`group/bhega relative flex items-stretch text-left ${full ? "w-full" : "w-auto min-w-[240px]"} ${s.box} border transition-colors duration-[250ms]
          focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[var(--bhega-ink)] ${
          joined
            ? "bg-inverse border-[var(--bhega-inverse)]"
            : "bg-[var(--bhega-primary)] hover:bg-[var(--bhega-primary-hover)] text-[var(--bhega-on-primary)] border-[var(--bhega-primary)] hover:border-[var(--bhega-primary-hover)]"
        }`}
      >
        {punch > 0 && <span key={`ring-${punch}`} className="bhega-btn-ring absolute inset-0 pointer-events-none" aria-hidden />}
        <span className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
          {nudging && (
            <motion.span
              className="absolute inset-y-0 left-0 w-[40%]"
              style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,.55), transparent)" }}
              initial={{ x: "-100%" }}
              animate={{ x: "250%" }}
              transition={{ duration: 0.7, delay: 0.1, ease: "easeInOut" }}
              onAnimationComplete={() => setNudging(false)}
            />
          )}
        </span>

        <span className={`relative flex items-center justify-center shrink-0 ${s.fist} ${joined ? "bg-[var(--bhega-surface-subtle)]" : "bg-[var(--bhega-on-primary)] text-[var(--bhega-primary)]"}`}>
          <motion.span className="inline-block" variants={reduce ? undefined : { hover: { y: -2 } }} transition={HOVER_SPRING}>
            <motion.span className="inline-block" animate={fist}>
              <span aria-hidden key={`fist-${punch}`} className={punch ? "fist-punch inline-block" : "inline-block"}>✊</span>
            </motion.span>
          </motion.span>
        </span>

        <span className="relative flex-1 min-w-0 flex flex-col justify-center px-3.5 py-1.5">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={joined ? "joined" : "idle"}
              className="flex flex-col min-w-0"
              initial={reduce ? { opacity: 0 } : { y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1, transition: labelTransition }}
              exit={reduce ? { opacity: 0, transition: { duration: 0.15 } } : { y: -8, opacity: 0, transition: { duration: 0.12 } }}
            >
              <span className={`font-extrabold uppercase tracking-[0.08em] leading-tight ${s.line1} flex items-center gap-1.5 whitespace-nowrap`}>
                {joined ? <><DrawnCheck /> You&apos;re in</> : "Bhega"}
              </span>
              {joined ? (
                month && <span className={`${s.line2} font-semibold leading-tight text-[var(--bhega-on-inverse)] truncate`}>{month}</span>
              ) : (
                <span className={`${s.line2} font-semibold leading-tight truncate`}>I want to buy this</span>
              )}
            </motion.span>
          </AnimatePresence>
        </span>

        <span className="relative flex flex-col items-end justify-center pr-3.5 shrink-0">
          <span className={`num font-extrabold leading-none ${s.count} overflow-hidden`} data-testid={countTestId}>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span key={count} className="inline-block" initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -10, opacity: 0 }}>
                {count}
              </motion.span>
            </AnimatePresence>
          </span>
          {size !== "sm" && <span className="text-[length:var(--fs-micro)] font-semibold leading-tight mt-0.5 opacity-80" aria-hidden>buyers</span>}
        </span>
      </motion.button>
      <span className="sr-only" aria-live="polite">{announce}</span>
      {sheetOpen && typeof document !== "undefined" && createPortal(
        <IntentDateModal
          productId={id}
          city={city}
          productName={name}
          onClose={() => setSheetOpen(false)}
          onSaved={(m) => { activity?.markJoined?.(id, m); afterJoin(m === "ASAP" ? "ASAP" : formatMonthLabel(m)); }}
          onDecideLater={() => { activity?.markJoined?.(id, "pending"); afterJoin(""); }}
        />,
        document.body,
      )}
    </>
  );
}
